// Auto-detect bounding boxes of objects on a sheet using connected components.
// Algorithm:
//   1. Sample corners → estimate background color
//   2. Threshold pixels by distance from background
//   3. Connected-components (8-connectivity) via iterative flood fill on a downsampled mask
//   4. Filter components by min size, compute bounding boxes
//   5. Optionally merge boxes that overlap significantly

export function detectBoxes(imageData, opts = {}) {
  const {
    tolerance = 28,        // 0-255 distance threshold
    minSizePct = 0.005,    // minimum component area as % of total image
    paddingPx = 6,         // padding added around each detected box
    downsample = 2,        // process at 1/N resolution for speed
  } = opts;

  const { width: W, height: H, data } = imageData;
  const dw = Math.max(1, Math.floor(W / downsample));
  const dh = Math.max(1, Math.floor(H / downsample));

  // Build downsampled mask
  const mask = new Uint8Array(dw * dh);
  const bg = estimateBackgroundColor(data, W, H);

  for (let y = 0; y < dh; y++) {
    for (let x = 0; x < dw; x++) {
      const srcX = Math.min(W - 1, x * downsample);
      const srcY = Math.min(H - 1, y * downsample);
      const i = (srcY * W + srcX) * 4;
      const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
      // If pixel is fully transparent, treat as background
      if (a < 32) { mask[y * dw + x] = 0; continue; }
      const d = Math.abs(r - bg.r) + Math.abs(g - bg.g) + Math.abs(b - bg.b);
      mask[y * dw + x] = d > tolerance * 3 ? 1 : 0;
    }
  }

  // Morphological close: dilate then erode to bridge small gaps
  const closed = morphClose(mask, dw, dh, 2);

  // Connected components (8-connectivity) iterative flood fill using stack
  const labels = new Int32Array(dw * dh);
  const boxes = [];
  let label = 0;
  const minArea = Math.max(64, Math.floor(dw * dh * minSizePct));

  for (let i = 0; i < closed.length; i++) {
    if (closed[i] === 1 && labels[i] === 0) {
      label++;
      // iterative flood fill
      const stack = [i];
      let minX = dw, minY = dh, maxX = 0, maxY = 0, area = 0;
      while (stack.length) {
        const p = stack.pop();
        if (labels[p] !== 0) continue;
        if (closed[p] !== 1) continue;
        labels[p] = label;
        const px = p % dw;
        const py = (p - px) / dw;
        if (px < minX) minX = px;
        if (py < minY) minY = py;
        if (px > maxX) maxX = px;
        if (py > maxY) maxY = py;
        area++;
        // 8 neighbors
        if (px > 0) stack.push(p - 1);
        if (px < dw - 1) stack.push(p + 1);
        if (py > 0) stack.push(p - dw);
        if (py < dh - 1) stack.push(p + dw);
        if (px > 0 && py > 0) stack.push(p - dw - 1);
        if (px < dw - 1 && py > 0) stack.push(p - dw + 1);
        if (px > 0 && py < dh - 1) stack.push(p + dw - 1);
        if (px < dw - 1 && py < dh - 1) stack.push(p + dw + 1);
      }
      if (area >= minArea) {
        // upscale to original coordinates
        const x = Math.max(0, minX * downsample - paddingPx);
        const y = Math.max(0, minY * downsample - paddingPx);
        const x2 = Math.min(W, (maxX + 1) * downsample + paddingPx);
        const y2 = Math.min(H, (maxY + 1) * downsample + paddingPx);
        boxes.push({ x, y, w: x2 - x, h: y2 - y, area: area * downsample * downsample });
      }
    }
  }

  // Sort: top-to-bottom, then left-to-right with row tolerance
  boxes.sort((a, b) => {
    const rowTol = Math.min(a.h, b.h) * 0.5;
    if (Math.abs(a.y - b.y) > rowTol) return a.y - b.y;
    return a.x - b.x;
  });

  return { boxes, bg };
}

function morphClose(mask, w, h, radius) {
  const dilated = morphOp(mask, w, h, radius, 'dilate');
  return morphOp(dilated, w, h, radius, 'erode');
}

function morphOp(src, w, h, r, mode) {
  const out = new Uint8Array(src.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let v = mode === 'dilate' ? 0 : 1;
      for (let dy = -r; dy <= r; dy++) {
        const ny = y + dy;
        if (ny < 0 || ny >= h) continue;
        for (let dx = -r; dx <= r; dx++) {
          const nx = x + dx;
          if (nx < 0 || nx >= w) continue;
          const s = src[ny * w + nx];
          if (mode === 'dilate') { if (s === 1) { v = 1; dy = r + 1; break; } }
          else { if (s === 0) { v = 0; dy = r + 1; break; } }
        }
      }
      out[y * w + x] = v;
    }
  }
  return out;
}

// Estimate background by sampling four corner regions and taking the median-ish color
export function estimateBackgroundColor(data, W, H) {
  const samples = [];
  const patch = Math.max(4, Math.floor(Math.min(W, H) * 0.04));
  const corners = [
    [0, 0],
    [W - patch, 0],
    [0, H - patch],
    [W - patch, H - patch],
  ];
  for (const [cx, cy] of corners) {
    for (let y = cy; y < cy + patch; y++) {
      for (let x = cx; x < cx + patch; x++) {
        const i = (y * W + x) * 4;
        samples.push([data[i], data[i + 1], data[i + 2]]);
      }
    }
  }
  // Median per channel
  const r = median(samples.map(s => s[0]));
  const g = median(samples.map(s => s[1]));
  const b = median(samples.map(s => s[2]));
  return { r, g, b };
}

function median(arr) {
  const s = arr.slice().sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
}
